<template>
  <div class="app-version-page">
    <el-card class="upload-card" shadow="never">
      <div slot="header" class="card-header">
        <div>
          <h2>版本管理</h2>
          <p>工具上传 APK 后，填写文件名和版本信息即可发布。</p>
        </div>
      </div>

      <el-form
        ref="versionForm"
        :model="form"
        label-width="110px"
        class="version-form"
        @submit.native.prevent="submitVersion"
      >
        <el-form-item label="APK文件名" required>
          <el-input
            v-model.trim="form.apkName"
            :disabled="uploading"
            maxlength="255"
            placeholder="例如：universe-release.apk"
          >
            <template slot="prepend">https://universe.top/apk/</template>
          </el-input>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="versionCode" required>
              <el-input
                v-model.trim="form.versionCode"
                :disabled="uploading"
                placeholder="例如：101"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="versionName" required>
              <el-input
                v-model.trim="form.versionName"
                :disabled="uploading"
                maxlength="50"
                placeholder="例如：1.0.1"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="更新方式" required>
          <el-select
            v-model="form.forceUpdate"
            :disabled="uploading"
            placeholder="请选择更新方式"
          >
            <el-option label="推荐更新" :value="false" />
            <el-option label="强制更新" :value="true" />
          </el-select>
        </el-form-item>

        <el-form-item label="更新说明" required>
          <el-input
            v-model="form.releaseNotes"
            type="textarea"
            :rows="5"
            :disabled="uploading"
            placeholder="请输入本次更新内容"
          />
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            native-type="submit"
            :loading="uploading"
            :disabled="uploading"
          >
            {{ uploading ? '正在发布' : '登记并发布' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="history-card" shadow="never">
      <div slot="header" class="history-header">
        <div>
          <h2>历史版本</h2>
          <p>按 versionCode 从高到低排列。</p>
        </div>
        <el-button
          size="small"
          icon="el-icon-refresh"
          :loading="listLoading"
          @click="loadVersions"
        >
          刷新
        </el-button>
      </div>

      <el-table
        v-loading="listLoading"
        :data="versions"
        border
        empty-text="暂无历史版本"
        style="width: 100%"
      >
        <el-table-column label="状态" width="82" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.isCurrent" size="mini" type="success">当前</el-tag>
            <span v-else class="history-label">历史</span>
          </template>
        </el-table-column>
        <el-table-column prop="versionCode" label="versionCode" width="120" />
        <el-table-column prop="versionName" label="versionName" width="130" />
        <el-table-column label="更新方式" width="110" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.forceUpdate" size="mini" type="danger">强制更新</el-tag>
            <el-tag v-else size="mini">推荐更新</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新说明" min-width="220">
          <template slot-scope="scope">
            <div class="release-notes">{{ scope.row.releaseNotes }}</div>
          </template>
        </el-table-column>
        <el-table-column label="APK" width="90" align="center">
          <template slot-scope="scope">
            <a
              v-if="safeApkUrl(scope.row.apkUrl)"
              :href="safeApkUrl(scope.row.apkUrl)"
              target="_blank"
              rel="noopener noreferrer"
            >下载</a>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdBy" label="发布人" width="110" />
        <el-table-column prop="createdAt" label="发布时间" width="170" />
        <el-table-column label="操作" width="90" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="danger"
              size="mini"
              plain
              :loading="deletingId === scope.row.id"
              :disabled="deletingId !== null"
              @click="deleteVersion(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { createAppVersionApi } from '@/api/appVersion'

export default {
  name: 'AppVersion',
  data () {
    return {
      api: null,
      form: {
        apkName: '',
        versionCode: '',
        versionName: '',
        releaseNotes: '',
        forceUpdate: false
      },
      uploading: false,
      listLoading: false,
      deletingId: null,
      versions: []
    }
  },
  created () {
    this.api = createAppVersionApi(this.$axios)
    this.loadVersions()
  },
  methods: {
    validateForm () {
      const apkName = String(this.form.apkName || '').trim()
      if (!apkName || /[\/\\]/.test(apkName) || !/\.apk$/i.test(apkName)) {
        return '请输入正确的 APK 文件名，例如 universe-release.apk'
      }
      if (!/^[1-9]\d*$/.test(String(this.form.versionCode))) {
        return 'versionCode 必须是正整数'
      }
      if (!this.form.versionName) {
        return '请输入 versionName'
      }
      if (!String(this.form.releaseNotes).trim()) {
        return '请输入更新说明'
      }
      return ''
    },
    submitVersion () {
      const errorMessage = this.validateForm()
      if (errorMessage) {
        this.$message.error(errorMessage)
        return
      }

      this.uploading = true
      this.api.upload({
        apkName: this.form.apkName,
        versionCode: this.form.versionCode,
        versionName: this.form.versionName,
        releaseNotes: this.form.releaseNotes,
        forceUpdate: this.form.forceUpdate
      }).then(() => {
        this.$message.success('发布成功，版本已立即生效')
        this.resetForm()
        return this.loadVersions()
      }).catch(error => {
        this.$message.error(error && error.message ? error.message : '发布失败，请稍后重试')
      }).then(() => {
        this.uploading = false
      })
    },
    resetForm () {
      this.form.apkName = ''
      this.form.versionCode = ''
      this.form.versionName = ''
      this.form.releaseNotes = ''
      this.form.forceUpdate = false
    },
    loadVersions () {
      this.listLoading = true
      return this.api.list().then(data => {
        this.versions = Array.isArray(data) ? data : []
      }).catch(error => {
        this.$message.error(error && error.message ? error.message : '版本列表加载失败')
      }).then(() => {
        this.listLoading = false
      })
    },
    deleteVersion (row) {
      this.$confirm(
        '确认删除版本 ' + row.versionName + '（versionCode ' + row.versionCode + '）吗？',
        '删除版本',
        {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.deletingId = row.id
        return this.api.deleteVersion(row.id)
      }).then(() => {
        this.$message.success('删除成功')
        return this.loadVersions()
      }).catch(error => {
        if (error === 'cancel' || error === 'close') {
          return
        }
        this.$message.error(error && error.message ? error.message : '删除失败，请稍后重试')
      }).then(() => {
        this.deletingId = null
      })
    },
    safeApkUrl (url) {
      try {
        const parsed = new URL(String(url || ''), window.location.href)
        return parsed.protocol === 'https:' ? parsed.href : ''
      } catch (error) {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.app-version-page {
  box-sizing: border-box;
  min-width: 760px;
  padding: 22px;
  background: #f5f7fa;
}

.upload-card,
.history-card {
  margin-bottom: 20px;
}

.card-header,
.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.card-header h2,
.history-header h2 {
  margin: 0;
  color: #1f2d3d;
  font-size: 22px;
  line-height: 1.4;
}

.card-header p,
.history-header p {
  margin: 8px 0 0;
  color: #606266;
  line-height: 1.6;
}

.version-form {
  padding-top: 4px;
}

.release-notes {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.history-label {
  color: #909399;
}

@media (max-width: 900px) {
  .app-version-page {
    min-width: 0;
    padding: 12px;
  }

  .history-header {
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/main/appVersion.vue